import { useState } from "react";
import api, { apiError } from "../lib/api";
import { useData } from "../hooks/useData";
import { useAuth } from "../context/AuthContext";
import { PageHeader, ExportButtons, StatCard, SearchBar, Loader, Empty } from "../components/ui-kit";
import { Modal, Field } from "../components/Modal";
import { exportExcel, exportPDF } from "../lib/exporters";
import { Plus, Trash2, Pencil, Eye, CheckCircle2, ListTodo, Clock, AlertTriangle, CircleDot } from "lucide-react";
import { toast } from "sonner";

const STATUTS = [
  ["a_faire", "À faire", "rgba(160,160,160,.18)", "#C9C9C0"],
  ["en_cours", "En cours", "rgba(255,213,79,.16)", "#FFD54F"],
  ["en_attente", "En attente", "rgba(255,152,0,.16)", "#FF9800"],
  ["terminee", "Terminée", "rgba(76,175,80,.16)", "#4CAF50"],
  ["annulee", "Annulée", "rgba(120,120,120,.16)", "#9E9E9E"],
];
const PRIORITES = [
  ["faible", "Faible", "rgba(160,160,160,.18)", "#C9C9C0"],
  ["normale", "Normale", "rgba(33,150,243,.16)", "#2196F3"],
  ["haute", "Haute", "rgba(255,152,0,.16)", "#FF9800"],
  ["urgente", "Urgente", "rgba(244,67,54,.16)", "#F44336"],
];
const stMap = Object.fromEntries(STATUTS.map(([k, l, bg, c]) => [k, { l, bg, c }]));
const prMap = Object.fromEntries(PRIORITES.map(([k, l, bg, c]) => [k, { l, bg, c }]));

const EMPTY = { titre: "", description: "", responsable: "", priorite: "normale", date_limite: "", statut: "a_faire", commentaire: "" };

const Badge = ({ map, k }) => {
  const x = map[k] || { l: k, bg: "var(--tm-surface-2)", c: "var(--tm-muted)" };
  return <span className="tm-badge" style={{ background: x.bg, color: x.c }}>{x.l}</span>;
};

const isOverdue = (t) =>
  t.date_limite && !["terminee", "annulee"].includes(t.statut) &&
  new Date(t.date_limite) < new Date(new Date().toISOString().slice(0, 10));

export default function Taches() {
  const { can, isAdmin } = useAuth();
  const { data, loading, refetch } = useData("/taches");

  const [query, setQuery] = useState("");
  const [fResp, setFResp] = useState("");
  const [fStatut, setFStatut] = useState("");
  const [fPrio, setFPrio] = useState("");
  const [fDate, setFDate] = useState("");

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [viewT, setViewT] = useState(null);

  const openNew = () => { setEditId(null); setForm({ ...EMPTY }); setOpen(true); };
  const openEdit = (t) => {
    setEditId(t.id);
    setForm({
      titre: t.titre || "", description: t.description || "", responsable: t.responsable || "",
      priorite: t.priorite || "normale", date_limite: t.date_limite ? t.date_limite.slice(0, 10) : "",
      statut: t.statut || "a_faire", commentaire: t.commentaire || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.titre.trim()) return toast.error("Indiquez le titre de la tâche");
    const payload = { ...form, date_limite: form.date_limite ? new Date(form.date_limite).toISOString() : "" };
    try {
      if (editId) { await api.put(`/taches/${editId}`, payload); toast.success("Tâche modifiée"); }
      else { await api.post("/taches", payload); toast.success("Tâche créée"); }
      setOpen(false); refetch();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  const cloturer = async (t) => {
    if (!window.confirm(`Clôturer la tâche ${t.numero} ? La date/heure de clôture sera enregistrée.`)) return;
    try { await api.put(`/taches/${t.id}/cloturer`); toast.success("Tâche clôturée (Terminée)"); refetch(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Supprimer la tâche ${t.numero} ?`)) return;
    try { await api.delete(`/taches/${t.id}`); toast.success("Supprimée"); refetch(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const fmt = (s) => (s ? new Date(s).toLocaleDateString("fr-FR") : "—");
  const fmtDT = (s) => (s ? new Date(s).toLocaleString("fr-FR") : "—");

  const responsables = [...new Set(data.map((t) => t.responsable).filter(Boolean))].sort();
  const q = query.trim().toLowerCase();
  const filtered = data.filter((t) => {
    if (q && ![t.numero, t.titre, t.responsable].filter(Boolean).some((v) => String(v).toLowerCase().includes(q))) return false;
    if (fResp && t.responsable !== fResp) return false;
    if (fStatut && t.statut !== fStatut) return false;
    if (fPrio && t.priorite !== fPrio) return false;
    if (fDate && (!t.date_limite || t.date_limite.slice(0, 10) > fDate)) return false;
    return true;
  });

  const stats = {
    total: data.length,
    a_faire: data.filter((t) => t.statut === "a_faire").length,
    en_cours: data.filter((t) => t.statut === "en_cours").length,
    terminee: data.filter((t) => t.statut === "terminee").length,
    retard: data.filter((t) => isOverdue(t)).length,
  };

  const cols = [
    { header: "N°", value: (r) => r.numero },
    { header: "Création", value: (r) => fmt(r.date_creation) },
    { header: "Titre", value: (r) => r.titre },
    { header: "Responsable", value: (r) => r.responsable || "" },
    { header: "Priorité", value: (r) => prMap[r.priorite]?.l || r.priorite },
    { header: "Échéance", value: (r) => fmt(r.date_limite) },
    { header: "Statut", value: (r) => stMap[r.statut]?.l || r.statut },
    { header: "Clôture", value: (r) => (r.date_cloture ? fmtDT(r.date_cloture) : "") },
  ];

  const canEditRow = (t) => can("taches", "edit") && (t.statut !== "terminee" || isAdmin);

  return (
    <div>
      <PageHeader title="Tâches à faire" subtitle="Créez, attribuez et suivez l'avancement des tâches jusqu'à leur clôture.">
        {can("taches", "export") && <ExportButtons onExcel={() => exportExcel("taches_tiramessi", cols, data)} onPDF={() => exportPDF("Tâches TIRAMESSI", cols, data)} />}
        {can("taches", "create") && <button className="tm-btn" onClick={openNew} data-testid="add-tache-btn"><Plus size={16} /> Nouvelle tâche</button>}
      </PageHeader>

      <div className="grid grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
        <StatCard label="Total tâches" value={stats.total} icon={ListTodo} testid="stat-total" />
        <StatCard label="À faire" value={stats.a_faire} icon={CircleDot} testid="stat-a-faire" />
        <StatCard label="En cours" value={stats.en_cours} icon={Clock} accent="#FFD54F" testid="stat-en-cours" />
        <StatCard label="Terminées" value={stats.terminee} icon={CheckCircle2} accent="#4CAF50" testid="stat-terminee" />
        <StatCard label="En retard" value={stats.retard} icon={AlertTriangle} accent="#F44336" testid="stat-retard" />
      </div>

      <SearchBar value={query} onChange={setQuery} placeholder="Rechercher par n°, titre, responsable..." testid="taches-search" count={filtered.length} total={data.length} />

      <div className="flex flex-wrap gap-3 mb-5">
        <select className="tm-input py-1.5 text-sm" style={{ width: "auto" }} value={fResp} onChange={(e) => setFResp(e.target.value)} data-testid="filter-responsable">
          <option value="">Tous les responsables</option>
          {responsables.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select className="tm-input py-1.5 text-sm" style={{ width: "auto" }} value={fStatut} onChange={(e) => setFStatut(e.target.value)} data-testid="filter-statut">
          <option value="">Tous les statuts</option>
          {STATUTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select className="tm-input py-1.5 text-sm" style={{ width: "auto" }} value={fPrio} onChange={(e) => setFPrio(e.target.value)} data-testid="filter-priorite">
          <option value="">Toutes les priorités</option>
          {PRIORITES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[var(--tm-muted)]">Échéance ≤</span>
          <input type="date" className="tm-input py-1.5 text-sm" style={{ width: "auto" }} value={fDate} onChange={(e) => setFDate(e.target.value)} data-testid="filter-date" />
        </div>
        {(fResp || fStatut || fPrio || fDate) && (
          <button className="tm-btn-ghost tm-btn py-1.5 text-sm" onClick={() => { setFResp(""); setFStatut(""); setFPrio(""); setFDate(""); }} data-testid="clear-filters">Réinitialiser</button>
        )}
      </div>

      {loading ? <Loader /> : data.length === 0 ? <Empty text="Aucune tâche enregistrée." /> : filtered.length === 0 ? <Empty text="Aucun résultat pour cette recherche/ces filtres." /> : (
        <div className="tm-card overflow-x-auto" data-testid="taches-table">
          <table className="tm-table">
            <thead>
              <tr>
                <th>Création</th><th>N°</th><th>Titre</th><th>Responsable</th>
                <th>Priorité</th><th>Échéance</th><th>Statut</th><th>Clôture</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} data-testid={`tache-row-${t.id}`} style={isOverdue(t) ? { boxShadow: "inset 3px 0 0 #F44336" } : undefined}>
                  <td className="text-[var(--tm-muted)] whitespace-nowrap">{fmt(t.date_creation)}</td>
                  <td className="tabular" style={{ color: "var(--tm-gold)" }}>{t.numero}</td>
                  <td className="font-semibold max-w-[220px] truncate" title={t.titre}>{t.titre}</td>
                  <td>{t.responsable || "—"}</td>
                  <td><Badge map={prMap} k={t.priorite} /></td>
                  <td className="whitespace-nowrap" style={{ color: isOverdue(t) ? "#F44336" : "var(--tm-muted)" }}>{fmt(t.date_limite)}</td>
                  <td><Badge map={stMap} k={t.statut} /></td>
                  <td className="text-[var(--tm-muted)] whitespace-nowrap text-xs">{t.date_cloture ? fmtDT(t.date_cloture) : "—"}</td>
                  <td className="text-right whitespace-nowrap">
                    <button onClick={() => setViewT(t)} className="text-[var(--tm-muted)] hover:text-[var(--tm-gold)] mr-3" data-testid={`view-tache-${t.id}`} title="Voir"><Eye size={16} /></button>
                    {canEditRow(t) && <button onClick={() => openEdit(t)} className="text-[var(--tm-muted)] hover:text-[var(--tm-gold)] mr-3" data-testid={`edit-tache-${t.id}`} title="Modifier"><Pencil size={15} /></button>}
                    {can("taches", "validate") && t.statut !== "terminee" && t.statut !== "annulee" && (
                      <button onClick={() => cloturer(t)} className="tm-btn-ghost tm-btn text-xs mr-2 py-1" data-testid={`cloturer-tache-${t.id}`} title="Clôturer"><CheckCircle2 size={13} /> Clôturer</button>
                    )}
                    {can("taches", "delete") && <button onClick={() => remove(t)} className="text-[var(--tm-muted)] hover:text-[#F44336]" data-testid={`delete-tache-${t.id}`} title="Supprimer"><Trash2 size={15} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit modal */}
      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Modifier la tâche" : "Nouvelle tâche"} testid="tache-modal">
        <form onSubmit={save}>
          <Field label="Titre de la tâche"><input className="tm-input" required value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} data-testid="tache-titre" /></Field>
          <Field label="Description détaillée"><textarea className="tm-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="tache-description" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Responsable (nom)"><input className="tm-input" placeholder="Nom de la personne" value={form.responsable} onChange={(e) => setForm({ ...form, responsable: e.target.value })} data-testid="tache-responsable" /></Field>
            <Field label="Priorité">
              <select className="tm-input" value={form.priorite} onChange={(e) => setForm({ ...form, priorite: e.target.value })} data-testid="tache-priorite">
                {PRIORITES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date limite"><input type="date" className="tm-input" value={form.date_limite} onChange={(e) => setForm({ ...form, date_limite: e.target.value })} data-testid="tache-date-limite" /></Field>
            <Field label="Statut">
              <select className="tm-input" value={form.statut} onChange={(e) => setForm({ ...form, statut: e.target.value })} data-testid="tache-statut">
                {STATUTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Commentaires (facultatif)"><textarea className="tm-input" rows={2} value={form.commentaire} onChange={(e) => setForm({ ...form, commentaire: e.target.value })} data-testid="tache-commentaire" /></Field>
          <button type="submit" className="tm-btn w-full mt-1" data-testid="tache-save">{editId ? "Enregistrer les modifications" : "Créer la tâche"}</button>
        </form>
      </Modal>

      {/* View modal */}
      <Modal open={!!viewT} onClose={() => setViewT(null)} title={viewT ? `Tâche ${viewT.numero}` : ""} testid="tache-view-modal">
        {viewT && (
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge map={prMap} k={viewT.priorite} /><Badge map={stMap} k={viewT.statut} />
              {isOverdue(viewT) && <span className="tm-badge" style={{ background: "rgba(244,67,54,.16)", color: "#F44336" }}>En retard</span>}
            </div>
            <p className="font-semibold text-lg">{viewT.titre}</p>
            {viewT.description && <p className="whitespace-pre-wrap" style={{ color: "var(--tm-beige)" }}>{viewT.description}</p>}
            <div className="grid grid-cols-2 gap-3 text-[var(--tm-muted)]">
              <p>Responsable : <span style={{ color: "var(--tm-beige)" }}>{viewT.responsable || "—"}</span></p>
              <p>Créée le : <span style={{ color: "var(--tm-beige)" }}>{fmtDT(viewT.date_creation)}</span></p>
              <p>Échéance : <span style={{ color: isOverdue(viewT) ? "#F44336" : "var(--tm-beige)" }}>{fmt(viewT.date_limite)}</span></p>
              <p>Clôture : <span style={{ color: "var(--tm-beige)" }}>{viewT.date_cloture ? fmtDT(viewT.date_cloture) : "—"}</span></p>
              <p>Créée par : <span style={{ color: "var(--tm-beige)" }}>{viewT.cree_par || "—"}</span></p>
            </div>
            {viewT.commentaire && <p className="italic text-[var(--tm-muted)]">« {viewT.commentaire} »</p>}
          </div>
        )}
      </Modal>
    </div>
  );
}
